package com.downdogapp.client.controllers.start

import com.downdogapp.*
import com.downdogapp.client.MANIFEST
import com.downdogapp.client.OfflinePractices
import com.downdogapp.client.Strings
import com.downdogapp.client.api.*
import com.downdogapp.client.api.LinkType.MENU
import com.downdogapp.client.controllers.MessageViewController
import com.downdogapp.client.controllers.SignInViewController
import com.downdogapp.client.controllers.TextInputViewController
import com.downdogapp.client.controllers.menu.LanguageViewController
import com.downdogapp.client.controllers.menu.SubscriptionViewController
import com.downdogapp.client.controllers.menu.UpdatePasswordViewController
import com.downdogapp.client.singleton.*
import com.downdogapp.client.singleton.Platform.WEB
import com.downdogapp.client.views.start.MenuPageView

object MenuPage : Page {

  override val view = MenuPageView()

  init {
    refreshPage()
  }

  override fun refreshPage() {
    view.refreshView()
  }

  override val title: String
    get() = MANIFEST.menuPageTitle

  override val shouldDisplay: Boolean
    get() = true

  fun logoutClicked() {
    if (Network.isOffline()) {
      App.alertNoInternet(message = Strings.LOGOUT_NO_INTERNET_MESSAGE)
    } else {
      logEvent("logged_out")
      if (App.platform == WEB) {
        App.logoutAccounts() // this also clears UserPrefs
        App.cred = null
      } else {
        val oldCred = App.cred
        App.logoutAccounts()
        UserPrefs.clear()
        App.cred = null
        OfflinePractices.onLogout()
        showSpinner()
        App.fetchManifest(oldCred = oldCred) {
          // Clear out saved practices and history in case they skip sign in
          HistoryUtil.fetchHistory()
          StartViewController.refreshView()
          hideSpinner()
          StartViewController.promptSignIn()
        }
      }
    }
  }

  fun hideSpinner() {
    StartViewController.hideSpinner()
  }

  fun showSpinner() {
    StartViewController.showSpinner()
  }

  fun shareClicked() {
    logClick("share", mapOf("url" to MANIFEST.shareUrl!!))
    App.showShareDialog(MANIFEST.shareUrl!!)
  }

  fun appleHealthClicked() {
    showSpinner()
    logClick("apple_health")
    Trackers.requestAppleHealthPermissions { success ->
      if (success) {
        if (Trackers.hasAppleHealthPermissions) {
          App.alert(message = Strings.CONNECTED_TO_APPLE_HEALTH_MESSAGE)
        } else {
          App.alert(message = Strings.MISSING_APPLE_HEALTH_PERMISSIONS_MESSAGE)
        }
      } else {
        App.alert(message = Strings.ERROR_CONNECTING_TO_APPLE_HEALTH_MESSAGE)
      }
      refreshPage()
      hideSpinner()
    }
  }

  fun restorePurchaseClicked() {
    App.restorePurchases()
  }

  fun signInClicked() {
    StartViewController.promptSignIn()
  }

  fun promotionalEmailsClicked(turnOn: Boolean) {
    showSpinner()
    Network.post(ChangePromotionalEmailStatusRequest(turnOn)) { json ->
      if (json != null) {
        MANIFEST.receivingPromotionalEmails = turnOn
        view.refreshView()
        hideSpinner()
      } else {
        App.alertNoInternet {
          hideSpinner()
        }
      }
    }
  }

  fun membershipClicked() {
    App.goToPurchase()
  }

  fun subscriptionClicked(subscription: Subscription) {
    App.pushViewController(SubscriptionViewController(subscription))
  }

  fun emailClicked() {
    App.pushViewController(
      TextInputViewController(
        title = Strings.EDIT_EMAIL,
        inputLabel = Strings.EMAIL,
        initialValue = MANIFEST.email,
        submitCallback = MenuPage::emailUpdated
      )
    )
  }

  private fun emailUpdated(value: String) {
    val email = value.trim()
    if (email == MANIFEST.email) {
      return
    } else if (!SignInViewController.isValidEmail(email)) {
      App.alert(message = Strings.INVALID_EMAIL_MESSAGE)
    } else {
      showSpinner()
      Network.post(UpdateEmailRequest(email)) { json ->
        if (json == null) {
          App.alertNoInternet {
            hideSpinner()
          }
        } else {
          val response = Status.fromJson(json)
          if (!response.success) {
            response.message?.let {
              App.alert(message = it) {
                hideSpinner()
              }
            }
          } else {
            App.fetchManifest {
              hideSpinner()
            }
          }
        }
      }
    }
  }

  private fun postUnlinkFacebookRequest() {
    Network.post(UnlinkFacebookRequest()) { json ->
      if (json == null) {
        App.alertNoInternet {
          hideSpinner()
        }
      } else {
        App.fetchManifest {
          hideSpinner()
        }
      }
    }
  }

  fun facebookClicked() {
    if (MANIFEST.hasPassword) {
      postUnlinkFacebookRequest()
    } else {
      App.pushViewController(
        UpdatePasswordViewController(
          unlinkMessage = Strings.UNLINK_FROM_FACEBOOK_MESSAGE,
          onPasswordChanged = {
            hideSpinner()
            postUnlinkFacebookRequest()
          }
        )
      )
    }
  }

  private fun postUnlinkGoogleRequest() {
    Network.post(UnlinkGoogleRequest()) { json ->
      if (json == null) {
        App.alertNoInternet {
          hideSpinner()
        }
      } else {
        App.fetchManifest {
          hideSpinner()
        }
      }
    }
  }

  fun googleClicked() {
    if (MANIFEST.hasPassword) {
      postUnlinkGoogleRequest()
    } else {
      App.pushViewController(
        UpdatePasswordViewController(
          unlinkMessage = Strings.UNLINK_FROM_GOOGLE_MESSAGE,
          onPasswordChanged = {
            hideSpinner()
            postUnlinkGoogleRequest()
          }
        )
      )
    }
  }

  private fun postUnlinkAppleRequest() {
    Network.post(UnlinkAppleRequest()) { json ->
      if (json == null) {
        App.alertNoInternet {
          hideSpinner()
        }
      } else {
        App.fetchManifest {
          hideSpinner()
        }
      }
    }
  }

  fun appleClicked() {
    if (MANIFEST.hasPassword) {
      postUnlinkAppleRequest()
    } else {
      App.pushViewController(
        UpdatePasswordViewController(
          unlinkMessage = Strings.UNLINK_FROM_APPLE_MESSAGE,
          onPasswordChanged = {
            hideSpinner()
            postUnlinkAppleRequest()
          }
        )
      )
    }
  }

  fun passwordClicked() {
    App.pushViewController(UpdatePasswordViewController())
  }

  fun languageClicked() {
    if (Network.isOffline()) {
      App.alert(message = Strings.CHANGE_LANGUAGE_NO_INTERNET_MESSAGE)
    } else {
      App.pushViewController(LanguageViewController())
    }
  }

  fun clearAllHistoryClicked() {
    App.alert(
      title = Strings.DELETE_HISTORY,
      message = Strings.CLEAR_PRACTICE_HISTORY_CONFIRM_MESSAGE,
      negativeButton = AlertAction(Strings.CANCEL),
      positiveButton = AlertAction(Strings.OK) {
        Network.post(DeleteAllHistoryRequest()) { json ->
          if (json != null) {
            HistoryUtil.fetchHistory()
          } else {
            App.alertNoInternet {
              hideSpinner()
            }
          }
        }
      }
    )
  }

  fun deleteAccountClicked() {
    App.alert(
      title = Strings.DELETE_ACCOUNT,
      message = Strings.DELETE_ACCOUNT_CONFIRM_MESSAGE,
      negativeButton = AlertAction(Strings.CANCEL),
      positiveButton = AlertAction(Strings.OK) {
        val willBeChargedAgain = MANIFEST.subscriptions.any {
          it.willBeChargedAgain
        }

        val hasUnstoppablePendingPayments = MANIFEST.subscriptions.any {
          it.willBeChargedAgain && !it.canSetAutoRenew
        }

        val deleteAccountReconfirmMessage =
          if (willBeChargedAgain)
            if (hasUnstoppablePendingPayments) {
              Strings.DELETE_ACCOUNT_WITHOUT_STOPPING_PAYMENTS_CONFIRM_MESSAGE
            } else {
              Strings.DELETE_ACCOUNT_AND_STOP_PAYMENTS_CONFIRM_MESSAGE
            }
          else
            Strings.DELETE_ACCOUNT_RECONFIRM_MESSAGE

        // This duration must be longer than the one in AppPage.alert,
        // otherwise the second alert gets destroyed when we take down the first alert.
        App.runAfter(seconds(0.1)) {
          App.alert(
            title = Strings.DELETE_ACCOUNT,
            message = deleteAccountReconfirmMessage,
            negativeButton = AlertAction(Strings.CANCEL),
            positiveButton = AlertAction(Strings.OK) {
              showSpinner()
              Network.post(DeleteUserRequest()) { json ->
                if (json != null) {
                  hideSpinner()
                  logoutClicked()
                } else {
                  App.alertNoInternet {
                    hideSpinner()
                  }
                }
              }
            }
          )
        }
      }
    )
  }

  fun enterCodeClicked() {
    logClick("enter_code")
    App.pushViewController(
      TextInputViewController(
        title = Strings.ENTER_CODE,
        inputLabel = Strings.SUBMIT,
        submitCallback = MenuPage::enteredCode
      )
    )
  }

  private fun enteredCode(code: String) {
    if (code.isNotEmpty()) {
      showSpinner()
      Network.post(SubmitCodeRequest(code)) { json ->
        if (json != null) {
          val response = Status.fromJson(json)
          logEvent(
            "submit_code",
            mapOf(
              "code" to code,
              "success" to response.success.toString()
            )
          )
          App.fetchManifest {
            App.alert(message = response.message) {
              hideSpinner()
            }
          }
        } else {
          App.alertNoInternet {
            hideSpinner()
          }
        }
      }
    }
  }

  fun linkClicked(link: Link) {
    logEvent("link", mapOf("link" to link.id))
    Network.post(RecordLinkClickedRequest(link.id, MENU))
    App.openFirstValidUrl(link.urls)
  }

  fun supportClicked() {
    App.pushViewController(
      MessageViewController(message = MANIFEST.menuContactMessage!!)
    )
  }
}
